import { API_BASE } from './api-base';
/**
 * Browser-owned Gemini Live voice call. The server only mints short-lived
 * ephemeral tokens (/api/voice/ephemeral-token); the WebSocket runs straight
 * from the browser to Google. Modeled on wixel-fs's GeminiTransport +
 * RealtimeSession, collapsed into one class while we're single-provider.
 *
 * The voice model carries one substantive tool, ask_symphony: anything the
 * assistant loop must answer is handed over (the host fetches it), and the
 * answer is returned as a tool RESULT the model reads out. The model itself is
 * ears and mouth — for now. See the voice-channel design doc for the direct
 * tool plane that follows.
 *
 * ask_symphony is declared NON_BLOCKING and answered as a two-part generator
 * (`willContinue`): an immediate `answered: false` acknowledgement, then the
 * real answer on the same call id scheduled INTERRUPT. That is what keeps a
 * 10-30s assistant turn survivable on a live call — the model is never frozen,
 * so the user can interrupt, change subject, or stack a second request while
 * the first runs. The earlier design held the function response open (blocking)
 * because a bare "pending" ack once let the model invent calendar data aloud;
 * `answered: false` carries that same guarantee as DATA rather than as a rule
 * the model has to remember, and the prompt's narration discipline backs it.
 *
 * Invariants carried over from wixel-fs production:
 *   - connect() config must echo the token-baked constraints exactly
 *     (system instruction, voice, tool declarations) or Gemini closes with 1007.
 *   - The token is minted to outlive the longest call the server allows, and is
 *     NEVER refreshed on a timer. Resuming a session is not a "use", so the one
 *     credential covers every reconnect; the old 5-minute TTL plus a refresh
 *     timer is what tore healthy calls down at the 4-minute mark.
 *   - A close we did not ask for reconnects with the latest resumption handle,
 *     reusing the credential already held — transparent to the user.
 *   - The greeting is sent right after the session opens, BEFORE mic capture
 *     starts: awaiting the mic first leaves a gap where an early close nulls
 *     the session out from under the greeting call. What was BUFFERED while the
 *     socket handshook is drained only from the first audible chunk (see
 *     MicCapture.drain) — draining silence into the greeting turn made the model
 *     answer a user who had not spoken.
 */
import { MicCapture, PcmPlayback, thinkingCueActive } from './voice-audio';
import { loadInputDevice, loadOutputDevice, loadVolume } from './voice-audio-prefs';
import { AUDIBLE_RMS, MicSilenceWatch } from './voice-silence';
import { buildVoiceGreetingTurn, type SpokenGender } from './voice-system-prompt';
import { normalizeProvider, type VoiceProvider } from './voice-catalog';
import type { VoiceCredential, VoiceToolResponse, VoiceTransport, VoiceTransportEvents } from './voice-transport';

// After end_call fires, let the model's spoken goodbye finish playing before
// tearing down — capped so a stuck playback can't keep the call alive.
const END_GOODBYE_MAX_MS = 6_000;
// How many recent spoken turns to replay as context into a fresh session after
// a voice switch. Bounded so the replay doesn't bloat time-to-first-token.
const MAX_HISTORY_TURNS = 16;
// Transparent reconnects for unexpected closes (Google pod cycling, transient
// 1011 "service unavailable"). Bounded so a real outage surfaces as a failed
// call instead of an endless reconnect loop. The budget resets on each
// successful turn, so a long healthy call isn't starved.
const MAX_RESUME_ATTEMPTS = 3;
const RESUME_BACKOFF_MS = 600;
// When an ask outruns this, push a silent context note so the model can answer
// "is it done yet?" honestly instead of guessing. Sits above the observed p90
// (~29s) so a normal turn never trips it. Silent: the user hears nothing.
const ASK_SLOW_NUDGE_MS = 32_000;
// How long before the server's call-duration cap to ask the model to wrap up,
// so the cap lands as a spoken goodbye rather than as a re-mint that 429s
// mid-sentence.
const WIND_DOWN_LEAD_MS = 45_000;
// Ceiling on the per-event transcript suppression counters. Suppression exists
// to avoid DOUBLE-WRITING turns the assistant loop already persisted; an
// unbounded counter would instead swallow unrelated turns, so it is capped.
const MAX_PENDING_SUPPRESSIONS = 2;
// How often the input level is sampled for the silence watch. Deliberately a poll rather than
// the level callback: a microphone that delivers NO audio at all also delivers no callbacks,
// and that is precisely the case worth catching.
const SILENCE_SAMPLE_MS = 500;
/**
 * How long after delivering a result we still treat it as "about to be spoken", even with nothing
 * playing yet. The model needs a beat to turn a tool response into audio, and a second result
 * landing inside that beat is exactly the collision this window exists to catch. It also makes the
 * state self-healing: if the model never speaks the result at all, the next one interrupts normally
 * instead of being queued behind a turn that will never end.
 */
const RESULT_SPEECH_LEAD_MS = 2_000;

export type VoiceCallStatus = 'connecting' | 'live' | 'ended' | 'error';

export interface VoiceCallEvents {
  onStatusChange(status: VoiceCallStatus, detail?: string): void;
  /** Streaming transcription deltas, one call per delta. */
  onTranscriptDelta(speaker: 'user' | 'orion', text: string): void;
  /**
   * A completed spoken turn, aggregated from deltas. Fired user-first.
   * Turns the assistant loop persists itself (an ask's request and its spoken
   * answer) are suppressed — see `suppressUserTurns` / `suppressAgentTurns`.
   */
  onTurnTranscript(speaker: 'user' | 'orion', text: string): void;
  onTurnComplete(): void;
  /**
   * The voice model handed a substantive request to the full assistant. The
   * host resolves it and calls deliverAnswer(callId, text). Several may be in
   * flight at once — NON_BLOCKING lets the user stack requests — so the callId
   * identifies which one an answer belongs to.
   */
  onAsk(callId: string, request: string): void;
  /**
   * The model called one of its OWN tools (calendar, tasks, routines, navigation) rather than
   * delegating. The host runs it against the voice tool plane and returns the outcome via
   * deliverToolResult(). Same generator shape as an ask: acknowledged instantly, answered later.
   */
  onVoiceTool(callId: string, tool: string, params: Record<string, unknown>): void;
  /**
   * The model recorded a permanent fact about the USER — how to say their name, or which
   * grammatical forms to address them in. Fire-and-forget on purpose: the model has already
   * confirmed out loud and adjusted for the rest of this call from its own session context, so the
   * write only decides whether the fact survives to the NEXT call. It must never make the user wait
   * and must never fail the call. Same relay as onVoiceTool, no generator and no result.
   */
  onProfileFact(tool: string, params: Record<string, unknown>): void;
  /**
   * The conversation this call belongs to. Resolved before the first token is minted, because the
   * session's tool surface is assembled per-conversation server-side — and because everything the
   * call says or does has to land in the same thread as the user's chat.
   */
  resolveConversationId(): Promise<string | null>;
  /** In-flight ask count changed (drives the working indicator). */
  onWorkingChange(inFlight: number): void;
  /**
   * Whether the audible thinking cue should be sounding. NOT the same signal as `onWorkingChange`:
   * the on-screen indicator stays up for the whole wait, while the tick-tock stands down whenever
   * the agent is speaking for itself. See `thinkingCueActive`.
   */
  onThinkingCueChange(active: boolean): void;
  /**
   * The model muted the mic via voice (one-way — the user unmutes from the
   * UI). The host syncs its mute state so the on-screen control reflects it.
   */
  onMutedChange(muted: boolean): void;
  /**
   * The microphone has gone quiet for long enough that the call is probably deaf — or has
   * started being heard again. Fired only on a transition. See lib/voice-silence.ts.
   */
  onMicSilentChange(silent: boolean): void;
  /**
   * The server's ceiling for this call, as returned by the mint. Fired once per
   * mint; the UI keeps the FIRST value, because the deadline is measured from the
   * start of the call rather than from the latest re-mint.
   */
  onDurationCap?(maxCallDurationMs: number): void;
}

/**
 * What the mint handed back. The provider field is what picks the transport; a response without
 * one is Gemini, which is every token this app minted before Grok existed.
 */
type TokenResponse = VoiceCredential;

/**
 * The providers this client has a transport for. The browser ships both, so it declares both; a
 * client that only speaks one names only that one and the mint degrades accordingly.
 */
const SUPPORTED_PROVIDERS: VoiceProvider[] = ['gemini', 'grok'];

const ASK_SYMPHONY = 'ask_symphony';
const END_CALL = 'end_call';
const MUTE_MICROPHONE = 'mute_microphone';
const SET_VOICE_GENDER = 'set_voice_gender';
const SET_USER_GENDER = 'set_user_gender';
const SET_USER_NAME_PRONUNCIATION = 'set_user_name_pronunciation';

/**
 * Everything Gemini bakes into an ephemeral token and checks the `setup` frame against — model,
 * voice, system instruction and every tool declaration including each `behavior` — as one comparable
 * string. Two credentials with the same fingerprint can share a session; two that differ cannot, and
 * trying is close code 1007.
 *
 * Ordering is deliberately NOT normalised away: the tool array's order is part of the contract the
 * client echoes, so a reordered array must read as a different set of constraints, because it is one.
 */
export function constraintsFingerprint(credential: VoiceCredential): string {
  return JSON.stringify([
    credential.model,
    credential.voiceName,
    credential.systemInstruction,
    credential.toolDeclarations ?? [],
  ]);
}

function newCallId(): string {
  // randomUUID needs a secure context; the fallback keeps a call working on a
  // plain-http origin rather than throwing at construction.
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  return `call-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

type PendingAsk = {
  /** Gemini's function-call id — the answer must come back on this id. */
  id: string;
  /** The tool the call was made against, echoed back on the closing response. */
  name: string;
  request: string;
  nudgeTimer: ReturnType<typeof setTimeout> | null;
};

export class VoiceCall {
  private events: VoiceCallEvents;
  // The displayed product name — the voice self-name follows it.
  private productName: 'Symphony';
  private mic = new MicCapture();
  private playback = new PcmPlayback();
  /**
   * The live wire. Provider-specific; everything else in this class is not — see
   * `lib/voice-transport.ts` for why the session is single and only the transport forks.
   */
  private transport: VoiceTransport | null = null;
  private credential: TokenResponse | null = null;
  private resumptionHandle: string | null = null;
  /** The provider's last word on whether the live session can be resumed. Null until it says. */
  private serverResumable: boolean | null = null;
  /**
   * How many sessions this CREDENTIAL has been used to open, reset on every fresh mint.
   *
   * The mint stamps `uses: 1`, on the documented premise that resuming does not spend a use. When a
   * call dies with 1011 "Token has been used too many times", this count is the difference between
   * "the premise is wrong" and "our reopens were not really resumes" — and without it that question
   * is unanswerable after the fact, which is exactly where one investigation stalled.
   */
  private opensOnCredential = 0;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshPending = false;
  private resumeAttempts = 0;

  /**
   * Every reconnect this call attempted. Distinct from `resumeAttempts`, which is the retry budget
   * and RESETS on each completed turn — so at end-of-call that one usually reads 0 and says nothing
   * about a call that reconnected four times. This one never resets.
   */
  private reconnects = 0;

  /**
   * The reconnect facts, for the lifecycle report. Read at end-of-call, so the server can tell a
   * spent credential from a fresh session on drifted constraints — they close with the same code, and
   * telling them apart from the browser console alone was not possible after the fact.
   *
   * `opens` is the one that settles it: it counts sessions opened on the CURRENT credential, so
   * `opens >= 2` is direct proof that reusing a `uses: 1` token across a resume DID work.
   */
  get diagnostics(): { opens: number; reconnects: number; resumeAttempts: number; serverResumable?: boolean } {
    return {
      opens: this.opensOnCredential,
      reconnects: this.reconnects,
      resumeAttempts: this.resumeAttempts,
      ...(this.serverResumable === null ? {} : { serverResumable: this.serverResumable }),
    };
  }
  private ended = false;
  private greetingSent = false;
  private userTurnBuffer = '';
  private orionTurnBuffer = '';
  private inputLevel = 0;
  /**
   * Loudest mic frame since the last turn flush — the evidence that the user actually SPOKE.
   *
   * Gemini's input transcription invents turns out of silence: a deaf microphone produced "花壇 使い
   * 方" and "Che?" attributed to a user who was speaking neither Japanese nor Italian, and those got
   * PERSISTED into the chat forever. A transcript is a claim about what was said; a claim with no
   * audible input behind it is not one we should keep.
   */
  private userTurnPeakRms = 0;
  /**
   * Whether the mic has ever reported a level at all.
   *
   * The drop below must require POSITIVE evidence of silence, never merely the absence of evidence
   * of speech. A capture graph that reports no levels — a browser without the worklet, a stubbed
   * mic, a path we have not thought of — would otherwise make every user turn look fabricated and
   * erase the user's half of the conversation. That is far worse than the fabrication it guards.
   */
  private micReportsLevels = false;
  private muted = false;
  private live = false;
  private silenceWatch = new MicSilenceWatch();
  private silenceTimer: ReturnType<typeof setInterval> | null = null;
  /**
   * Asks awaiting their real answer, by function-call id. NON_BLOCKING allows
   * more than one at a time (the user can stack requests while the first runs).
   */
  private pendingAsks = new Map<string, PendingAsk>();
  /**
   * Spoken turns to drop rather than persist, because the assistant loop
   * already wrote the authoritative version.
   *
   * Deliberately COUNTERS, not the boolean window this used to be. The old
   * window opened on an ask and closed on the user's next utterance — which
   * only worked while an ask FROZE the model. Non-blocking asks invite the user
   * to speak mid-wait, and that utterance would have closed the window early,
   * letting the answer readback through as a second copy of a reply the harness
   * had already persisted. Counters are anchored to the events that create the
   * duplicate instead of to user behaviour, so mid-wait speech persists
   * normally (the loop never saw it) while the request and the readback don't.
   */
  private suppressUserTurns = 0;
  private suppressAgentTurns = 0;
  /** Last emitted cue state, so the host is told only on a transition. */
  private cueSounding = false;
  /**
   * When a result was last handed over to be SPOKEN (an answer or a tool outcome), or null once that
   * speech has finished. This is what tells a second result to wait its turn instead of cutting the
   * first one off mid-sentence — see `resultScheduling`.
   */
  private speakingResultSince: number | null = null;
  // The voice gender currently baked into the token. set_voice_gender flips it
  // and reconnects so the new voice is minted in.
  private voiceGender: SpokenGender = 'feminine';
  /**
   * Whether the USER has switched the voice during this call. Until they do, the client asserts
   * nothing and the server picks from the agent's configured presentation — otherwise a client
   * default would silently override the identity the user set up, and a "Maestra" would answer in
   * a masculine voice.
   */
  private voiceGenderExplicit = false;
  // How the model should grammatically address the user (set_user_gender).
  // Lives only in live-session context, so it's re-sent after every reconnect.
  private addressedGender: SpokenGender | null = null;
  // How to SAY the user's name, as corrected DURING this call. The value the server already knew
  // arrives in `userContext`; this holds only an in-call correction, and is re-sent after a
  // reconnect for the same reason the gender is — a fresh socket loses session context, and the
  // re-mint that fed it may predate the correction.
  private namePronunciation: string | null = null;
  // Set while a set_voice_gender reconnect is in flight. The reconnect must NOT
  // resume via the handle: a resumed session restores the original session's
  // voice, silently ignoring the freshly-minted one (the switch appears to do
  // nothing). Dropping the handle starts a clean session in the new voice, at
  // the cost of in-call context — an acceptable trade for a deliberate switch.
  private switchingVoice = false;
  /**
   * The constraints the CURRENTLY-ESTABLISHED session was opened with — see
   * `constraintsFingerprint`. Null before the first session completes.
   *
   * This is the client half of the 1007 rule, and it exists because a real call died of it: Gemini
   * closes the socket with 1007 unless `setup` echoes the token's baked constraints exactly, and a
   * re-mint is an HTTP request that can land on any orion-web pod. If the constraints a re-mint
   * hands us differ from the ones the live session holds, resuming that session with them is a
   * guaranteed 1007 — so we open a CLEAN session instead and replay the context (below). Losing a
   * resume is a beat of setup latency; earning a 1007 ends the call.
   */
  private establishedConstraints: string | null = null;
  // Rolling in-call transcript, replayed as context into any session we did not
  // resume (a voice switch, or a re-mint whose constraints moved).
  private transcript: { speaker: 'user' | 'orion'; text: string }[] = [];
  // Drops mic audio (without touching the user-facing mute) across a voice
  // switch, so the freshly-injected "here's my new voice" line can't be cut
  // short by stray input or an eager follow-up before it even plays.
  private micGated = false;
  private awaitingPostSwitchTurn = false;
  private micGateTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Stable across this call's re-mints (pod cycles, reconnects, voice
   * switches), so the server can meter one CONVERSATION rather than counting
   * mints it can't tell apart. See lib/voice-quota.ts.
   *
   * Public because the lifecycle telemetry reports against this SAME id, which is what lets the
   * server's `[voice] call start` line join the client's connect/end reports.
   */
  readonly callId = newCallId();
  private readonly startedAt = Date.now();
  /**
   * Call timings, logged under `[voice:timing]`.
   *
   * Without these, "it took 40 seconds" can only be answered by guessing which of the mint, the
   * harness session fetch, the socket handshake or the model was slow — which is exactly what
   * happened, twice. Each phase is stamped so a report carries its own diagnosis.
   */
  private marks: Record<string, number> = {};
  /** Resolved once before the first mint; the session's tool surface is scoped to it. */
  private conversationId: string | null = null;
  private windDownTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Calls that were still in flight when a socket died. Function-call ids are SESSION-scoped, so
   * after a reconnect there is nothing left to answer: the generator opened with
   * `willContinue:true` can never be closed, and the model — told to say "still working" until a
   * result arrives — would say it for the rest of the call. Counted here so the fresh session can
   * be told the truth instead.
   */
  private interruptedPending = 0;
  private hardStopTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(events: VoiceCallEvents, productName: 'Symphony' = 'Symphony') {
    this.events = events;
    this.productName = productName;
  }

  async start(): Promise<void> {
    this.events.onStatusChange('connecting');
    // Mic starts in parallel with the token fetch: the permission prompt
    // appears immediately, and anything spoken while the socket handshakes
    // buffers inside MicCapture until drain() below — otherwise the first
    // words of the call are lost.
    // Remembered preferences apply from the first chunk, so a call opens on the devices and
    // volume the user already chose rather than snapping to them a moment later.
    this.playback.setVolume(loadVolume());
    void this.playback.setOutputDevice(loadOutputDevice());
    const micReady = this.mic.start(
      (b64) => {
        if (this.micGated) return;
        this.transport?.sendAudioChunk(b64);
      },
      (rms) => {
        this.inputLevel = rms;
        this.micReportsLevels = true;
        if (rms > this.userTurnPeakRms) this.userTurnPeakRms = rms;
      },
      loadInputDevice(),
    );
    try {
      // Before the first mint: the tool surface is assembled per-conversation server-side, so the
      // token can't be minted without knowing which conversation this call belongs to.
      this.mark('start');
      this.conversationId = await this.events.resolveConversationId();
      this.mark('conversation');
      this.credential = await this.fetchToken();
      this.opensOnCredential = 0;
      this.mark('token');
      this.scheduleWindDown(this.credential.maxCallDurationMs);
      // The model is asked to wrap up out loud shortly before the cap; the user
      // deserves to SEE it coming too, rather than have the call stop mid-sentence.
      if (this.credential.maxCallDurationMs) {
        this.events.onDurationCap?.(this.credential.maxCallDurationMs);
      }
      // The mic must be LIVE before we connect, because connecting is what makes the agent greet.
      // Previously the session opened first and this await came after: if the browser's permission
      // prompt wasn't answered immediately — and it is easy to miss — the agent introduced itself
      // into a void and then sat there unable to hear a word, with the call looking perfectly
      // healthy. Greeting someone you cannot hear is worse than making them wait a beat.
      this.events.onStatusChange('connecting', 'microphone');
      await micReady;
      this.mark('mic');
      if (this.ended) {
        this.mic.stop();
        return;
      }
      await this.openSession();
      this.mark('socket');
      this.logTimings();
      // Anything said during the permission prompt and the handshake is buffered; release it now
      // that there is a socket to send it on.
      this.mic.drain();
    } catch (err) {
      // A refused or missing microphone is the single most likely way a call fails, and it is
      // recoverable — but only if we say which one it was. Previously both fell into the generic
      // path and rendered as "Call failed — NotAllowedError", which tells the user nothing they
      // can act on.
      const name = (err as { name?: string } | null)?.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        this.fail('mic-denied');
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        this.fail('mic-missing');
      } else {
        this.fail(err instanceof Error ? err.message : String(err));
      }
    }
  }

  end(): void {
    if (this.ended) return;
    this.ended = true;
    this.flushTurnBuffers();
    this.clearPendingAsks();
    this.clearDurationTimers();
    this.stopSilenceWatch();
    this.gateMic(false);
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    this.mic.stop();
    void this.playback.close();
    const transport = this.transport;
    this.transport = null;
    transport?.close();
    this.events.onStatusChange('ended');
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.mic.setMuted(muted);
    // Muting mid-sentence should be audible immediately, so cut what the agent already has
    // queued rather than letting it talk over a mic the user just closed.
    if (muted) this.playback.stop();
    // Don't wait up to a tick to drop a "we can't hear you" that muting just made false.
    this.sampleSilence();
  }

  /**
   * Poll the input level and publish silence transitions. Started when the call goes live; the
   * watch itself decides what counts (lib/voice-silence.ts).
   */
  private startSilenceWatch(): void {
    if (this.silenceTimer) return;
    this.silenceWatch.reset();
    this.silenceTimer = setInterval(() => this.sampleSilence(), SILENCE_SAMPLE_MS);
  }

  private stopSilenceWatch(): void {
    if (this.silenceTimer) clearInterval(this.silenceTimer);
    this.silenceTimer = null;
    if (this.silenceWatch.isSilent) this.events.onMicSilentChange(false);
    this.silenceWatch.reset();
    this.live = false;
  }

  /**
   * Publish whether the tick-tock should be sounding. Called wherever either input can move —
   * an ask opening or closing, audio starting, a turn ending, and the silence poll as a backstop
   * for playback draining on its own.
   */
  private updateThinkingCue(): void {
    const next =
      !this.ended && thinkingCueActive(this.pendingAsks.size > 0, this.playback.isPlaying());
    if (next === this.cueSounding) return;
    this.cueSounding = next;
    this.events.onThinkingCueChange(next);
  }

  private sampleSilence(): void {
    this.updateThinkingCue();
    const changed = this.silenceWatch.observe({
      rms: this.inputLevel,
      live: this.live && !this.ended,
      muted: this.muted,
      agentSpeaking: this.playback.isPlaying(),
      working: this.pendingAsks.size > 0,
      now: Date.now(),
    });
    if (changed) this.events.onMicSilentChange(this.silenceWatch.isSilent);
  }

  /** Output volume 0..1, live — no graph rebuild. */
  setVolume(volume: number): void {
    this.playback.setVolume(volume);
  }

  /** Play a short tone through the selected speaker, to check it before trusting it. */
  async testSpeaker(): Promise<void> {
    await this.playback.playTestTone();
  }

  /** Route playback to a chosen speaker. No-op where the browser can't. */
  async setOutputDevice(deviceId: string | null): Promise<void> {
    await this.playback.setOutputDevice(deviceId);
  }

  /**
   * Switch microphone mid-call. On failure the previous device is restored and the error carries
   * `rolledBack` — the caller should stay silent in that case: the switch failed but the call is
   * fine, and saying otherwise would be misleading.
   */
  async setInputDevice(deviceId: string | null): Promise<void> {
    await this.mic.setDevice(deviceId);
    // A new device earns a fresh window: keeping the old verdict would accuse the mic the user
    // just picked of a fault the previous one had.
    this.inputLevel = 0;
    if (this.silenceWatch.isSilent) this.events.onMicSilentChange(false);
    this.silenceWatch.reset();
  }

  /**
   * Hand the real assistant answer back as the FINAL part of the ask's
   * generator response. Scheduled INTERRUPT so the model speaks it as soon as
   * it lands — unless the ask has been superseded, in which case it arrives
   * SILENT: the model absorbs the knowledge without derailing whatever the user
   * moved on to. Returning it as a tool RESULT (rather than injected user
   * content) is what makes it ground truth to read out.
   */
  deliverAnswer(callId: string, text: string, opts: { stale?: boolean } = {}): void {
    const pending = this.pendingAsks.get(callId);
    if (!pending) return;
    this.closePendingAsk(callId);
    // Defensive: never let markup reach the model even if the server missed it.
    const clean = text.replace(/<\/?voice>/gi, '').trim();
    // The spoken readback duplicates the reply the harness already persisted.
    if (!opts.stale) this.bumpSuppression('agent');
    this.sendToolResponse([
      {
        id: callId,
        // From the pending record rather than a constant: the response must close the SAME
        // call it answers, and this path is reachable for any pending call.
        name: pending.name,
        response: { answered: true, answer: clean },
        willContinue: false,
        scheduling: opts.stale ? 'SILENT' : this.resultScheduling(),
      },
    ]);
  }

  /**
   * How a result that IS meant to be spoken gets scheduled: right now, or after the current turn.
   *
   * INTERRUPT is right for the common case — the model is silent, or is only saying the filler it
   * was freed to say ("one sec, let me look"), and cutting that off with the real answer is the
   * whole point. It is wrong for one case, and that case shipped: `ask_symphony` is NON_BLOCKING, so
   * two asks can be in flight, and when they finish IN ORDER neither is "stale" (a stale answer is
   * one a LATER answer has already outrun). Both therefore interrupted, and the second cut the first
   * off mid-sentence — the user asked two things and heard one and a half.
   *
   * So a result that lands while an EARLIER result is being spoken waits its turn (WHEN_IDLE); the
   * model reads them one after the other and the user gets both. Note what this deliberately does
   * NOT do: it never suppresses, and it never queues behind ordinary agent chat — only behind
   * another answer, which is the only speech that carries information the user cannot get back.
   */
  private resultScheduling(): 'INTERRUPT' | 'WHEN_IDLE' {
    const queue = this.isSpeakingResult();
    this.speakingResultSince = Date.now();
    return queue ? 'WHEN_IDLE' : 'INTERRUPT';
  }

  /** True while a result we handed over is still being read out (or is about to start). */
  private isSpeakingResult(): boolean {
    if (this.speakingResultSince === null) return false;
    if (this.playback.isPlaying()) return true;
    return Date.now() - this.speakingResultSince < RESULT_SPEECH_LEAD_MS;
  }

  /** One place the transport is handed a function response, so no call site can forget the null check. */
  private sendToolResponse(responses: VoiceToolResponse[]): void {
    this.transport?.sendToolResponse(responses);
  }

  /** True while at least one ask is still awaiting its answer. */
  hasPendingAsk(callId: string): boolean {
    return this.pendingAsks.has(callId);
  }

  /**
   * Live audio snapshot for visualization, polled at animation rate —
   * deliberately not event-driven so React never re-renders per chunk.
   */
  getAudioState(): { inputLevel: number; outputLevel: number; orionSpeaking: boolean } {
    // Prefer the analyser's continuous per-frame level so the orb tracks the
    // voice smoothly; the field (`this.inputLevel`) is the ~8 Hz chunk RMS the
    // send path reports, kept as the fallback and still what silence detection
    // reads (see `sampleSilence`).
    return {
      inputLevel: this.mic.liveLevel() ?? this.inputLevel,
      outputLevel: this.playback.getLevel(),
      orionSpeaking: this.playback.isPlaying(),
    };
  }

  private async fetchToken(): Promise<TokenResponse> {
    const res = await fetch(
      `${API_BASE}/api/voice/ephemeral-token?name=${this.productName}&callId=${this.callId}` +
        // Which wires this build can actually drive. The mint honours a stored provider only if it
        // is named here, so a shipped binary that speaks one protocol is never handed a credential
        // for another — it degrades to Gemini instead of failing to connect.
        `&supports=${SUPPORTED_PROVIDERS.join(',')}` +
        (this.conversationId ? `&conversationId=${encodeURIComponent(this.conversationId)}` : '') +
        (this.voiceGenderExplicit ? `&voiceGender=${this.voiceGender}` : ''),
      {
        method: 'POST',
        credentials: 'include',
      },
    );
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as {
        error?: string;
        message?: string;
      } | null;
      throw new Error(body?.message ?? body?.error ?? `token request failed (${res.status})`);
    }
    return (await res.json()) as TokenResponse;
  }

  /**
   * Build the transport this credential calls for. Dynamic-imported so a user on one provider
   * never downloads the other's wire code — and so the Gemini SDK stays out of a Grok call's
   * critical path entirely.
   */
  private async buildTransport(
    provider: ReturnType<typeof normalizeProvider>,
    events: VoiceTransportEvents,
  ): Promise<VoiceTransport> {
    if (provider === 'grok') {
      const { GrokVoiceTransport } = await import('./voice-grok-transport');
      return new GrokVoiceTransport(events);
    }
    const { GeminiVoiceTransport } = await import('./voice-gemini-transport');
    return new GeminiVoiceTransport(events);
  }

  private async openSession(): Promise<void> {
    const credential = this.credential;
    if (!credential || this.ended) return;

    const provider = normalizeProvider(credential.provider);
    const fingerprint = constraintsFingerprint(credential);
    // A re-mint that came back with different baked constraints cannot be used to resume the live
    // session — that mismatch IS close code 1007. See `establishedConstraints`.
    const constraintsMoved =
      this.establishedConstraints !== null && this.establishedConstraints !== fingerprint;
    if (constraintsMoved) {
      console.warn('[voice] the re-mint changed the session constraints — starting a fresh session');
    }
    // Only Gemini can resume; a voice switch drops the handle on purpose, because a resumed
    // session restores the ORIGINAL voice and the switch would appear to do nothing.
    const resumptionHandle =
      provider === 'gemini' && !this.switchingVoice && !constraintsMoved ? this.resumptionHandle : null;

    const transportRef: { current: VoiceTransport | null } = { current: null };
    // Every callback checks it is still the LIVE transport: a refresh, a pod cycle or a voice
    // switch leaves the previous socket briefly draining, and its frames must not reach a session
    // that has already moved on.
    const isCurrent = () => !this.ended && this.transport === transportRef.current;
    const transport = await this.buildTransport(provider, {
      onAudio: (bytes) => {
        if (!isCurrent()) return;
        this.playback.enqueue(bytes);
        // The agent has started talking: stand the cue down now rather than at the next poll.
        this.updateThinkingCue();
      },
      onInputTranscript: (text) => {
        if (!isCurrent()) return;
        this.userTurnBuffer += text;
        this.events.onTranscriptDelta('user', text);
      },
      onOutputTranscript: (text) => {
        if (!isCurrent()) return;
        this.orionTurnBuffer += text;
        this.events.onTranscriptDelta('orion', text);
      },
      onInterrupted: () => {
        if (!isCurrent()) return;
        // Barge-in also ends the model's turn — flush what was actually said so the persisted
        // transcript reflects the cut, not the planned reply.
        this.playback.stop();
        // Barge-in ends whatever was being read out, so nothing is queued behind it any more.
        this.speakingResultSince = null;
        this.flushTurnBuffers();
        this.updateThinkingCue();
      },
      onTurnComplete: () => {
        if (isCurrent()) this.handleTurnComplete();
      },
      onToolCall: (call) => {
        if (isCurrent()) this.handleToolCall(call);
      },
      onResumptionHandle: (handle, resumable) => {
        this.resumptionHandle = handle;
        // Logged only on a CHANGE, because this arrives continuously through a call and the point is
        // the transition — a session that stops being resumable is the moment a later reopen turns
        // into a fresh session start, which is what spends the token's `uses` budget.
        if (resumable !== undefined && resumable !== this.serverResumable) {
          this.serverResumable = resumable;
          console.warn(`[voice] server says session resumable=${resumable}`);
        }
      },
      onError: (message) => {
        if (!this.ended && !this.refreshPending) console.warn(`[voice] ${provider} error:`, message);
      },
      onClose: (event) => {
        if (this.transport !== transportRef.current) return;
        this.handleClose(event);
      },
    });

    // Providers disagree on capture rate, and the mic was opened before we knew which one this
    // call would use. Correct it before a single chunk goes out at the wrong pitch.
    await this.mic.setSampleRate(transport.inputSampleRate).catch(() => {
      // A rebuild that fails leaves the previous graph running: worse audio beats no call.
    });

    await transport.open({ credential, resumptionHandle });
    if (this.ended) {
      transport.close();
      return;
    }
    transportRef.current = transport;
    this.transport = transport;
    const justSwitchedVoice = this.switchingVoice;
    this.opensOnCredential += 1;
    // `resumeRequested`, not `resumed`: this says we PRESENTED a handle, which is all a client can
    // know at connect time — the provider never acks the attempt. It was previously called `resumed`,
    // and reading that as "the provider resumed us" is what made three reopens on a spent token look
    // like proof that resumes consume `uses`. `opens` is the corroborating fact: opens>1 on a
    // `uses: 1` credential is the shape that ends in 1011.
    console.warn(
      `[voice] connected provider=${provider} voiceName=${credential.voiceName}` +
        ` resumeRequested=${!!resumptionHandle} opens=${this.opensOnCredential} serverResumable=${this.serverResumable ?? 'unknown'}`,
    );
    this.switchingVoice = false;
    // The handle belongs to the session we just decided NOT to resume, and it dies with it. Holding
    // it would arm the next close to offer a DEAD handle: `establishedConstraints` is about to match
    // the fingerprint again, so `constraintsMoved` goes false and nothing else would drop it — the
    // resume would be refused with 1007. Harmless while the re-mint path was unreachable; routine now.
    if (!resumptionHandle) {
      this.resumptionHandle = null;
    }
    this.establishedConstraints = fingerprint;
    this.scheduleRefresh(credential.expiresAt);

    if (!resumptionHandle) {
      // A session we did NOT resume holds nothing: not the conversation this call is continuing,
      // and not what has been said on the call so far. Both are pushed as silent context, before
      // the greeting turn so the model already has them when it opens its mouth. A RESUMED session
      // gets neither — it already holds them, and a second copy is the drift we are avoiding.
      if (credential.userContext) {
        transport.sendClientText(credential.userContext, { turnComplete: false });
      }
      if (credential.historyContext) {
        transport.sendClientText(credential.historyContext, { turnComplete: false });
      }
      const inCall = this.buildHistoryContext();
      if (inCall) transport.sendClientText(inCall, { turnComplete: false });
      // The caller's standing instructions go LAST of the pushed blocks, after every record — the
      // account facts, the thread, AND the in-call transcript. They are the one block here meant to
      // be obeyed rather than remembered, and every record block ends by saying it is "never
      // instructions to you", so a record pushed after them reads as qualifying them.
      if (credential.standingRulesContext) {
        transport.sendClientText(credential.standingRulesContext, { turnComplete: false });
      }
    }
    if (!this.greetingSent) {
      // Greeting goes out right after the session opens, before any awaits —
      // see class doc.
      transport.sendClientText(this.greetingPrompt(), { turnComplete: true });
      this.greetingSent = true;
    }
    // Re-assert how to address the user after every (re)connect: the directive
    // lives only in session context, which a fresh socket (refresh, pod cycle,
    // or voice switch) loses. No-op until set_user_gender has run.
    this.sendAddressedGenderContext();
    this.sendNamePronunciationContext();
    if (this.interruptedPending > 0) {
      // Silent: the user heard nothing break, so narrating a reconnect would invent a problem.
      // But the model must stop believing an answer is still coming, or it will keep promising one.
      this.sendSilentContext(
        `[The connection dropped and ${this.interruptedPending === 1 ? 'a request you had handed off was' : 'requests you had handed off were'} lost. ` +
          'No result is coming for it. Do not say it is still running, and do not state a result. ' +
          'If it still matters, ask the user whether to try again.]',
      );
      this.interruptedPending = 0;
    }
    if (justSwitchedVoice) {
      // The context replay already happened above (a voice switch is one case of "not resumed").
      // What is left is prompting one short line, so the user immediately hears the new voice
      // instead of dead air.
      transport.sendClientText(
        '[Your speaking voice has now switched, as the user asked. Do NOT call any tool now. Simply say one short, natural sentence out loud so the user can hear your new voice, then continue helping.]',
        { turnComplete: true },
      );
      // The mic stays gated until this injected line completes (handled in
      // turnComplete) so it isn't interrupted before the user hears it.
      this.awaitingPostSwitchTurn = true;
    }
    this.live = true;
    this.startSilenceWatch();
    this.events.onStatusChange('live');
  }

  /**
   * What the model is told to open with.
   *
   * Uses the caller's name when we have it, because "Hey Ronny" and "I'm here" are not the same
   * greeting — one is a person picking up, the other is a system announcing itself. Deliberately
   * warm rather than clipped: this is the first second of the call and it sets whether the thing
   * feels alive.
   */
  private greetingPrompt(): string {
    // Shared with the WhatsApp worker via common-lib, so a caller reaching the agent by phone is
    // greeted by the same agent in the same words. Do not fork this copy.
    return buildVoiceGreetingTurn(this.credential?.userName);
  }

  private mark(phase: string): void {
    this.marks[phase] = Date.now() - this.startedAt;
  }

  /** One line naming which phase of connecting actually cost the time. */
  private logTimings(): void {
    const m = this.marks;
    const span = (to: string, from: string) => Math.max(0, (m[to] ?? 0) - (m[from] ?? 0));
    console.warn(
      `[voice:timing] connect=${m.socket ?? 0}ms ` +
        `conversation=${span('conversation', 'start')}ms ` +
        `token=${span('token', 'conversation')}ms ` +
        `mic=${span('mic', 'token')}ms ` +
        `socket=${span('socket', 'mic')}ms`,
    );
  }

  private handleClose(closeEvent: { code: number; reason: string }): void {
    this.transport = null;
    if (this.ended) return;
    // Nothing pending can survive the socket — see `interruptedPending`.
    this.interruptedPending += this.pendingAsks.size;
    if (this.pendingAsks.size > 0) this.clearPendingAsks();
    // A token refresh we initiated already has a fresh credential staged.
    if (this.refreshPending) {
      this.refreshPending = false;
      this.openSession().catch(() => this.fail('reconnect after token refresh failed'));
      return;
    }
    // Any other close (Google pod cycle 1000, transient 1011, etc.): if we hold a resumption handle
    // and have budget, reconnect transparently. A short backoff lets a transient blip clear.
    //
    // The FIRST attempt reopens on the credential we already hold, because its baked constraints are
    // the ones the live session was opened with, so that resume cannot be refused for contradicting
    // them where a re-mint reads live state and could drift. Google's contract says "resuming a Live
    // API session does not count as a use", which would make one `uses: 1` token carry every resume.
    //
    // Production disagrees: 207 calls in three days died on `1011 Token has been used too many
    // times`, at the first reconnect. So reuse is a HEDGE, not the correctness property this comment
    // used to claim — and every later attempt re-mints. Which of the two readings is right is what
    // the `opens` / `serverResumable` diagnostic on the lifecycle event exists to settle; until it
    // does, do not delete either half.
    //
    // A provider with no resumption (Grok) therefore surfaces an unexpected drop as a failed call
    // rather than silently reopening into a session with no memory of the conversation — the
    // scheduled re-mint above is what keeps a HEALTHY call alive across credential expiry.
    const detail = `connection closed (code ${closeEvent.code}${
      closeEvent.reason ? `: ${closeEvent.reason}` : ''
    })`;
    // The close was previously only ever surfaced to the USER, as the failure banner — so a call that
    // reconnected its way to death left no record of WHY each socket dropped, only of the last one.
    // These four facts together are what identify a spent credential: the code/reason, how many
    // sessions this credential has opened, whether we had a handle to offer, and whether the provider
    // ever called the session resumable.
    console.warn(
      `[voice] socket closed code=${closeEvent.code} reason=${closeEvent.reason || '(none)'}` +
        ` opens=${this.opensOnCredential} hadHandle=${!!this.resumptionHandle}` +
        ` serverResumable=${this.serverResumable ?? 'unknown'} resumeAttempts=${this.resumeAttempts}/${MAX_RESUME_ATTEMPTS}`,
    );
    if (this.resumptionHandle && this.resumeAttempts < MAX_RESUME_ATTEMPTS) {
      this.resumeAttempts += 1;
      this.reconnects += 1;
      // The FIRST attempt reuses the credential: its baked constraints are the ones the live session
      // was opened with, so a resume can never be refused for contradicting them, and a re-mint reads
      // live state that can come back different.
      //
      // A LATER attempt means that reuse did not survive — the token is spent. Re-mint then, keyed on
      // the ATTEMPT NUMBER rather than on a rejected promise, because the failure never arrives as a
      // rejection: `openSession` resolves as soon as the socket opens, and the server closes it
      // afterwards with 1011. That close lands in this handler as a fresh event, so the re-mint that
      // used to sit in a `.catch` here was unreachable for the one failure it existed to fix — the
      // call simply retried the spent credential three times and died. iOS keys on the attempt number
      // (VoiceCallSession.swift) and is the only surface that survives past ten minutes in production.
      const reuseCredential = this.resumeAttempts === 1;
      setTimeout(() => {
        if (this.ended) return;
        // Let openSession decide what to do with the handle: matching constraints still resume,
        // drifted ones open a clean session and replay the call rather than dropping it.
        const remintAndOpen = () =>
          this.fetchToken()
            .then((fresh) => {
              if (this.ended) return;
              this.credential = fresh;
              this.opensOnCredential = 0;
              return this.openSession();
            })
            .catch(() => this.fail(detail));
        if (!reuseCredential) {
          void remintAndOpen();
          return;
        }
        // openSession awaits the handshake, so a connect that fails outright DOES reject here — and
        // that case recovers by re-minting rather than ending the call. It is not the path that
        // recovers a spent token: there the socket opens and the server closes it afterwards, which
        // arrives as another close and is handled by the attempt-number branch above.
        void this.openSession().catch(remintAndOpen);
      }, RESUME_BACKOFF_MS * this.resumeAttempts);
      return;
    }
    this.fail(detail);
  }

  /**
   * Land the server's call-duration cap as a conversation ending rather than a
   * failure. Shortly before the cap the model is asked to wrap up out loud; a
   * hard stop backs it, so a model that ignores the cue can't run past the
   * ceiling. Scheduled from the FIRST token only — the deadline is measured
   * from the start of the call, not from the latest re-mint.
   */
  private scheduleWindDown(maxCallDurationMs: number | undefined): void {
    if (!maxCallDurationMs || this.windDownTimer || this.hardStopTimer) return;
    const elapsed = Date.now() - this.startedAt;
    const untilWindDown = Math.max(1_000, maxCallDurationMs - elapsed - WIND_DOWN_LEAD_MS);
    this.windDownTimer = setTimeout(() => {
      if (this.ended) return;
      this.transport?.sendClientText(
        '[This call is reaching its time limit and will end in a moment. Say one short, warm goodbye now — mention they can start a new call any time. Do not call any tool.]',
        { turnComplete: true },
      );
    }, untilWindDown);
    this.hardStopTimer = setTimeout(
      () => this.end(),
      Math.max(2_000, maxCallDurationMs - elapsed),
    );
  }

  private clearDurationTimers(): void {
    if (this.windDownTimer) clearTimeout(this.windDownTimer);
    if (this.hardStopTimer) clearTimeout(this.hardStopTimer);
    this.windDownTimer = null;
    this.hardStopTimer = null;
  }

  /**
   * A healthy call is NEVER reconnected on a timer, and this method is the reason that sentence had
   * to be written down.
   *
   * It used to re-mint a minute before the token expired and close the live socket so `handleClose`
   * would reopen it. The token lasted 300s, so that fired at 4:00 of every call — and each teardown
   * risked the whole session: a re-mint reads live state (the harness tool surface, the agent's
   * gender, the self name), and a re-mint whose baked constraints have drifted CANNOT resume the
   * session it is replacing, because echoing constraints the session does not hold is close code
   * 1007. The safe fallback in that case is a clean session with a text replay of the call — which
   * is exactly what "the context resets at four minutes" was.
   *
   * The token now outlives the longest call we allow (see `TOKEN_TTL_SECONDS`), so there is nothing
   * to refresh: no timer, no teardown, no re-mint, and no window in which constraints can drift. A
   * socket that closes on its own is still recovered — reactively, by `handleClose`, reusing THIS
   * credential so the constraints are identical by construction.
   *
   * Kept as a named no-op rather than deleted at the call site: the wind-down and hard-stop timers
   * are what bound a call now, and a future change that reintroduces a refresh needs to read this.
   */
  private scheduleRefresh(_expiresAt: string): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  /** Route one function call from the model. Same set on every provider — the token declares it. */
  private handleToolCall(call: { id?: string; name?: string; args?: Record<string, unknown> }): void {
    if (call.name !== ASK_SYMPHONY) console.warn(`[voice] tool call: ${call.name}`, call.args ?? {});
    switch (call.name) {
      case ASK_SYMPHONY:
        this.handleAsk(call);
        return;
      case END_CALL:
        this.handleEndCall(call);
        return;
      case MUTE_MICROPHONE:
        this.handleMute(call);
        return;
      case SET_VOICE_GENDER:
        this.handleSetVoiceGender(call);
        return;
      case SET_USER_GENDER:
        this.handleSetUserGender(call);
        return;
      case SET_USER_NAME_PRONUNCIATION:
        this.handleSetUserNamePronunciation(call);
        return;
      default:
        // Anything else in the baked declarations is a harness-granted tool the model runs
        // itself. Unknown names can't occur — a provider only calls what the session declared.
        this.handleVoiceTool(call);
    }
  }

  private handleTurnComplete(): void {
    // A completed turn proves the (re)connection is healthy — refill the
    // reconnect budget so a long call survives repeated transient blips.
    this.resumeAttempts = 0;
    // The post-switch line finished playing — let the user back in.
    if (this.awaitingPostSwitchTurn) {
      this.awaitingPostSwitchTurn = false;
      this.gateMic(false);
    }
    this.speakingResultSince = null;
    this.flushTurnBuffers();
    this.updateThinkingCue();
    this.events.onTurnComplete();
  }

  /**
   * ask_symphony fired. Acknowledge IMMEDIATELY with `answered: false` and
   * `willContinue: true` — the model is freed to speak its "one sec" line and
   * keep listening, while holding, as data, the fact that it does not yet have
   * an answer. The host resolves the request and calls deliverAnswer(), which
   * closes the generator with the real result.
   */
  private handleAsk(call: { id?: string; name?: string; args?: Record<string, unknown> }): void {
    const request = typeof call.args?.request === 'string' ? call.args.request.trim() : '';
    const callId = call.id;
    if (!callId) {
      console.warn('[voice] ask arrived without a call id — cannot answer it');
      return;
    }
    if (!request) {
      this.ackAsk(callId, {
        answered: true,
        answer: "Sorry, I didn't catch that — could you say it again?",
      }, { final: true });
      return;
    }
    // The user's spoken request is persisted authoritatively by the ask route
    // as <voice>…</voice>; the filler line the model is about to say carries
    // no information. Drop both rather than double-write the first.
    this.bumpSuppression('user');
    this.bumpSuppression('agent');
    const pending: PendingAsk = {
      id: callId,
      name: ASK_SYMPHONY,
      request,
      nudgeTimer: setTimeout(() => this.nudgeSlowAsk(callId), ASK_SLOW_NUDGE_MS),
    };
    this.pendingAsks.set(callId, pending);
    this.events.onWorkingChange(this.pendingAsks.size);
    this.updateThinkingCue();
    this.ackAsk(callId, { answered: false, status: 'working' }, { final: false });
    this.events.onAsk(callId, request);
  }

  /**
   * One of the model's OWN tools. Same two-part shape as an ask, and for the same reason: these
   * are fast, but "fast" over a network is not "instant", and a model frozen mid-sentence is worse
   * than one that says "one sec". The ack carries `done: false` so a slow call can never be read
   * as a completed one.
   *
   * No suppression is registered here: unlike an ask, nothing else persists these turns, so what
   * the model says about a tool it ran is the only record of it.
   */
  private handleVoiceTool(call: { id?: string; name?: string; args?: Record<string, unknown> }): void {
    const callId = call.id;
    const tool = call.name;
    if (!callId || !tool) return;
    const pending: PendingAsk = {
      id: callId,
      name: tool,
      request: '',
      nudgeTimer: setTimeout(() => this.nudgeSlowAsk(callId), ASK_SLOW_NUDGE_MS),
    };
    this.pendingAsks.set(callId, pending);
    this.events.onWorkingChange(this.pendingAsks.size);
    this.updateThinkingCue();
    this.sendToolResponse([
      {
        id: callId,
        name: tool,
        response: { done: false, status: 'working' },
        willContinue: true,
        scheduling: 'SILENT',
      },
    ]);
    this.events.onVoiceTool(callId, tool, call.args ?? {});
  }

  /**
   * Close a voice tool's generator with what actually happened. `outcome` is the harness's
   * classified verdict — never raw tool prose, which a speech model would read aloud verbatim.
   */
  deliverToolResult(callId: string, outcome: { kind: string; result: unknown }): void {
    const pending = this.pendingAsks.get(callId);
    if (!pending) return;
    this.closePendingAsk(callId);
    this.sendToolResponse([
      {
        id: callId,
        name: pending.name,
        response: { done: true, ...outcome },
        willContinue: false,
        // Same rule as an answer: speak it now unless another result is still being spoken.
        scheduling: this.resultScheduling(),
      },
    ]);
  }

  /** Send one part of an ask's generator response. */
  private ackAsk(
    callId: string,
    response: Record<string, unknown>,
    opts: { final: boolean },
  ): void {
    this.sendToolResponse([
      {
        id: callId,
        name: ASK_SYMPHONY,
        response,
        willContinue: !opts.final,
        // The in-flight ack must NOT prompt speech — the model already
        // said its line. The final answer interrupts so it's spoken now.
        scheduling: opts.final ? 'INTERRUPT' : 'SILENT',
      },
    ]);
  }

  /**
   * An ask outran the nudge window. Tell the model silently so that, if the
   * user asks, it can say the work is still running rather than inventing a
   * result or claiming it finished.
   */
  private nudgeSlowAsk(callId: string): void {
    const pending = this.pendingAsks.get(callId);
    if (!pending || this.ended) return;
    pending.nudgeTimer = null;
    const what = pending.name === ASK_SYMPHONY ? 'The request you handed off' : 'The action you started';
    this.sendSilentContext(
      `[${what} is still running. You still do not have its result. ` +
        "If the user asks, say it's still in progress — never guess at the outcome.]",
    );
  }

  private closePendingAsk(callId: string): void {
    const pending = this.pendingAsks.get(callId);
    if (!pending) return;
    if (pending.nudgeTimer) clearTimeout(pending.nudgeTimer);
    this.pendingAsks.delete(callId);
    this.events.onWorkingChange(this.pendingAsks.size);
    this.updateThinkingCue();
  }

  private clearPendingAsks(): void {
    for (const pending of this.pendingAsks.values()) {
      if (pending.nudgeTimer) clearTimeout(pending.nudgeTimer);
    }
    this.pendingAsks.clear();
    this.suppressUserTurns = 0;
    this.suppressAgentTurns = 0;
    this.events.onWorkingChange(0);
    this.updateThinkingCue();
  }

  private bumpSuppression(which: 'user' | 'agent'): void {
    if (which === 'user') {
      this.suppressUserTurns = Math.min(this.suppressUserTurns + 1, MAX_PENDING_SUPPRESSIONS);
    } else {
      this.suppressAgentTurns = Math.min(this.suppressAgentTurns + 1, MAX_PENDING_SUPPRESSIONS);
    }
  }

  /** Answer a local call-control tool instantly — these never block. */
  private ackTool(
    call: { id?: string; name?: string },
    response: Record<string, unknown>,
  ): void {
    this.sendToolResponse([{ id: call.id, name: call.name, response }]);
  }

  private handleEndCall(call: { id?: string; name?: string }): void {
    this.ackTool(call, { ok: true });
    // The model says its goodbye in the same turn as the call; let that audio
    // drain before teardown so we don't clip it mid-word.
    const deadline = Date.now() + END_GOODBYE_MAX_MS;
    const tearDownWhenQuiet = () => {
      if (this.ended) return;
      if (this.playback.isPlaying() && Date.now() < deadline) {
        setTimeout(tearDownWhenQuiet, 200);
        return;
      }
      this.end();
    };
    setTimeout(tearDownWhenQuiet, 400);
  }

  private handleMute(call: { id?: string; name?: string }): void {
    this.setMuted(true);
    this.events.onMutedChange(true);
    this.ackTool(call, { ok: true });
  }

  private handleSetVoiceGender(call: {
    id?: string;
    name?: string;
    args?: Record<string, unknown>;
  }): void {
    const gender: SpokenGender = call.args?.gender === 'masculine' ? 'masculine' : 'feminine';
    this.ackTool(call, { ok: true });
    if (gender === this.voiceGender && this.voiceGenderExplicit) return;
    this.voiceGender = gender;
    // From here the user's choice outranks the agent's configured presentation, for this call.
    this.voiceGenderExplicit = true;
    // Gate the mic from now until the new voice's first line finishes, so it
    // plays without being interrupted. Capped, so a turn that never completes
    // can't leave the mic dead.
    this.gateMic(true);
    // Voice is baked into the token, so a switch means re-minting and
    // reconnecting (without the resume handle — see switchingVoice).
    void this.fetchToken()
      .then((fresh) => {
        if (this.ended) return;
        console.warn(`[voice] voice re-mint ok → ${fresh.voiceName}, reconnecting`);
        this.credential = fresh;
        this.opensOnCredential = 0;
        this.refreshPending = true;
        this.switchingVoice = true;
        this.transport?.close();
      })
      .catch((err) => {
        // Keep the current voice if the re-mint fails; the call is unaffected.
        console.warn('[voice] voice re-mint failed', err);
        this.gateMic(false);
      });
  }

  private handleSetUserGender(call: {
    id?: string;
    name?: string;
    args?: Record<string, unknown>;
  }): void {
    this.addressedGender = call.args?.gender === 'masculine' ? 'masculine' : 'feminine';
    this.sendAddressedGenderContext();
    // Answered instantly (this is a call control, not a generator) and persisted alongside, so the
    // NEXT call already knows. Without the write this fact evaporates with the socket — which is
    // exactly what it used to do.
    this.events.onProfileFact(SET_USER_GENDER, { gender: this.addressedGender });
    this.ackTool(call, { ok: true });
  }

  /**
   * Same shape as the gender control: instant local effect, durable write behind it. The value is a
   * RESPELLING — the only lever that changes what a realtime model actually says, since there is no
   * SSML or phoneme control on this wire.
   */
  private handleSetUserNamePronunciation(call: {
    id?: string;
    name?: string;
    args?: Record<string, unknown>;
  }): void {
    const raw = call.args?.pronunciation;
    const pronunciation = typeof raw === 'string' ? raw.replace(/\s+/g, ' ').trim().slice(0, 200) : '';
    if (!pronunciation) {
      this.ackTool(call, { ok: false, error: 'no pronunciation given' });
      return;
    }
    this.namePronunciation = pronunciation;
    this.sendNamePronunciationContext();
    this.events.onProfileFact(SET_USER_NAME_PRONUNCIATION, { pronunciation });
    this.ackTool(call, { ok: true });
  }

  private sendAddressedGenderContext(): void {
    if (!this.addressedGender) return;
    this.sendSilentContext(
      `[Address the user using ${this.addressedGender} grammatical forms from now on.]`,
    );
  }

  private sendNamePronunciationContext(): void {
    if (!this.namePronunciation) return;
    this.sendSilentContext(
      `[Say the user's name like this from now on: ${this.namePronunciation}. Never read that respelling aloud.]`,
    );
  }

  /**
   * Push text the model absorbs as context for future turns without taking a
   * speaking turn (sendClientContent with turnComplete:false) — used for state
   * the user shouldn't hear narrated back.
   */
  private sendSilentContext(text: string): void {
    this.transport?.sendClientText(text, { turnComplete: false });
  }

  /** Emit aggregated turn transcripts, user speech first. */
  private flushTurnBuffers(): void {
    const orionText = this.orionTurnBuffer.trim();
    this.orionTurnBuffer = '';
    // A "user turn" with no audible mic frame behind it was never spoken. Gemini's ASR hallucinates
    // from silence — a deaf mic yielded Japanese and Italian turns for a Hebrew/English speaker, and
    // they were persisted into the chat permanently. Dropped BEFORE the buffer is read, so a
    // fabrication reaches neither the transcript the UI shows, the conversation we store, nor the
    // context replayed into a fresh session (where it would go on to mislead the model too).
    //
    // The threshold is the silence detector's own `AUDIBLE_RMS`, so "the user said nothing" means
    // one thing across this file. Muted counts as inaudible for free: the mic sends no frames.
    const spoke = !this.micReportsLevels || this.userTurnPeakRms >= AUDIBLE_RMS;
    const userText = spoke ? this.userTurnBuffer.trim() : '';
    if (!spoke && this.userTurnBuffer.trim()) {
      console.warn(
        `[voice] dropped a transcribed user turn with no audible input: "${this.userTurnBuffer.trim().slice(0, 60)}"`,
      );
    }
    this.userTurnBuffer = '';
    this.userTurnPeakRms = 0;
    // Keep a rolling transcript for context replay after a voice switch —
    // captured regardless of suppression, since the goal is session continuity,
    // not persistence.
    if (userText) this.transcript.push({ speaker: 'user', text: userText });
    if (orionText) this.transcript.push({ speaker: 'orion', text: orionText });
    if (this.transcript.length > MAX_HISTORY_TURNS) {
      this.transcript = this.transcript.slice(-MAX_HISTORY_TURNS);
    }
    if (userText) {
      if (this.suppressUserTurns > 0) this.suppressUserTurns -= 1;
      else this.events.onTurnTranscript('user', userText);
    }
    if (orionText) {
      if (this.suppressAgentTurns > 0) this.suppressAgentTurns -= 1;
      else this.events.onTurnTranscript('orion', orionText);
    }
  }

  /** Gate (or release) mic input around a voice switch, with a safety cap. */
  private gateMic(on: boolean): void {
    this.micGated = on;
    if (this.micGateTimer) {
      clearTimeout(this.micGateTimer);
      this.micGateTimer = null;
    }
    if (on) {
      this.micGateTimer = setTimeout(() => this.gateMic(false), END_GOODBYE_MAX_MS);
    }
  }

  /** Recent spoken turns as a single context block for a fresh session. */
  private buildHistoryContext(): string | null {
    if (this.transcript.length === 0) return null;
    const lines = this.transcript
      .map((turn) => `${turn.speaker === 'user' ? 'User' : 'You'}: ${turn.text}`)
      .join('\n');
    return `[Context — this voice call is already in progress. What was said so far:\n${lines}\nContinue naturally with this in mind.]`;
  }

  private fail(detail: string): void {
    if (this.ended) return;
    this.ended = true;
    this.flushTurnBuffers();
    this.clearPendingAsks();
    this.clearDurationTimers();
    this.stopSilenceWatch();
    this.gateMic(false);
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    this.mic.stop();
    void this.playback.close();
    const transport = this.transport;
    this.transport = null;
    transport?.close();
    this.events.onStatusChange('error', detail);
  }
}
